/**
 * PayoffGraph — P&L across spot for the workspace basket.
 *   • At expiry (horizon = earliest leg expiry): profit area green, loss area red.
 *   • Today / what-if date (T+x): blue line, valued with each leg's IV (+ IV shift).
 *   • ±1σ / ±2σ expected-move bands at the horizon, breakevens, live index and the
 *     what-if spot; optional probability density of the horizon spot.
 *   • OI walls (background): call OI (left half of each strike slot) and put OI (right
 *     half) as faint bars scaled to the lower third of the plot; the three biggest strikes
 *     on each side are drawn stronger and the biggest is labelled — where the market's open interest is
 *     concentrated relative to your payoff.
 */
import React, { useMemo } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceArea, ResponsiveContainer,
} from 'recharts';
import { Box } from '@mui/material';
import { WsLeg, positionPnl, sdBands, lnCdf } from './wsMath';
import { WsColors, Ccy, fmtMoney, fmtStrike } from './wsUi';
import { USD_TO_INR } from '../../../utils/constants';

interface Props {
  legs: WsLeg[];
  spot: number;
  whatIfSpot: number;
  atMs: number;
  horizon: number;
  ivShift: number;
  cv: number;
  atmIv: number;
  breakevens: number[];
  ccy: Ccy;
  showProb: boolean;
  /** Open interest by strike for the expiry on screen (contracts), and whether to draw it. */
  oiRows?: Array<{ strike: number; ceOi: number; peOi: number }>;
  showOi?: boolean;
  c: WsColors;
  /** Close / roll preview: the position as it is now, drawn dashed at its own horizon. */
  compareLegs?: WsLeg[];
  compareHorizon?: number;
}

const PayoffGraph: React.FC<Props> = ({ legs, spot, whatIfSpot, atMs, horizon, ivShift, cv, atmIv, breakevens, ccy, showProb, oiRows, showOi, c, compareLegs, compareHorizon }) => {
  const cmp = !!compareLegs?.length && compareHorizon != null;
  const fx = ccy === 'INR' ? USD_TO_INR : 1;
  const tH = Math.max(0, horizon - Date.now()) / (365 * 86400e3);
  const sigma = atmIv > 0 ? atmIv : 0.5;
  const bands = sdBands(spot, sigma, tH);

  const data = useMemo(() => {
    // Frame the strikes and ±2.5σ — wide enough for the whole shape, tight enough to read.
    // Frame both the previewed and the current position (a closed leg's strike still matters).
    const strikes = [...legs, ...(cmp ? compareLegs! : [])].filter(l => l.enabled).map(l => l.strike);
    const sd = sigma * Math.sqrt(Math.max(tH, 1 / 365));
    let lo = Math.min(spot * Math.exp(-2.5 * sd), ...strikes);
    let hi = Math.max(spot * Math.exp(2.5 * sd), ...strikes);
    const pad = (hi - lo) * 0.06;
    lo -= pad; hi += pad;
    const grid = Array.from({ length: 181 }, (_, i) => lo + ((hi - lo) * i) / 180);
    const step = grid.length > 1 ? grid[1] - grid[0] : 1;
    return grid.map(s => {
      const exp = positionPnl(legs, s, horizon, cv) * fx;
      const today = positionPnl(legs, s, atMs, cv, ivShift) * fx;
      const prob = showProb ? (lnCdf(s + step / 2, spot, sigma, tH) - lnCdf(s - step / 2, spot, sigma, tH)) : 0;
      const before = cmp ? positionPnl(compareLegs!, s, compareHorizon!, cv) * fx : null;
      return { s, exp, today, before, pos: Math.max(0, exp), neg: Math.min(0, exp), prob };
    });
  }, [legs, spot, sigma, tH, horizon, atMs, ivShift, cv, fx, showProb, cmp, compareLegs, compareHorizon]);

  const yMin = Math.min(0, ...data.map(d => Math.min(d.exp, d.today, d.before ?? 0)));
  const yMax = Math.max(0, ...data.map(d => Math.max(d.exp, d.today, d.before ?? 0)));
  const yPad = (yMax - yMin) * 0.08 || 1;
  const xMin = data[0]?.s ?? spot * 0.9, xMax = data[data.length - 1]?.s ?? spot * 1.1;
  const inX = (x: number) => x >= xMin && x <= xMax;
  const showWhatIf = Math.abs(whatIfSpot - spot) / spot > 0.0005 && inX(whatIfSpot);
  const maxProb = Math.max(1e-9, ...data.map(d => d.prob));

  const tickMoney = (v: number) => fmtMoney(v / fx, ccy);

  // OI walls in view: bar width from the listed strike spacing; top-3 per side = walls.
  const oi = useMemo(() => {
    if (!showOi || !oiRows?.length) return null;
    const rows = oiRows.filter(r => r.strike >= xMin && r.strike <= xMax && (r.ceOi > 0 || r.peOi > 0));
    if (!rows.length) return null;
    const ks = rows.map(r => r.strike).sort((a, b) => a - b);
    let step = Infinity;
    for (let i = 1; i < ks.length; i++) step = Math.min(step, ks[i] - ks[i - 1]);
    if (!Number.isFinite(step)) step = (xMax - xMin) / 40;
    const half = Math.min(step * 0.42, (xMax - xMin) / 60);
    const max = Math.max(1, ...rows.map(r => Math.max(r.ceOi, r.peOi)));
    const top = (k: 'ceOi' | 'peOi') => new Set([...rows].sort((a, b) => b[k] - a[k]).slice(0, 3).filter(r => r[k] > 0).map(r => r.strike));
    const top1 = (k: 'ceOi' | 'peOi') => rows.reduce((p, r) => (r[k] > p[k] ? r : p), rows[0]).strike;
    return { rows, half, max, ceTop: top('ceOi'), peTop: top('peOi'), ce1: top1('ceOi'), pe1: top1('peOi') };
  }, [showOi, oiRows, xMin, xMax]);
  const oiFloor = yMin - yPad;
  const oiY = (v: number) => oiFloor + (v / (oi?.max || 1)) * (yMax + yPad - oiFloor) * 0.3;

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 180 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 22, right: 14, bottom: 4, left: 4 }}>
          <CartesianGrid stroke={c.border} vertical={false} />
          <XAxis dataKey="s" type="number" domain={[xMin, xMax]} tickCount={6}
            tickFormatter={(v: number) => fmtStrike(Math.round(v))} tick={{ fontSize: 10, fill: c.dim }} stroke={c.border} />
          <YAxis yAxisId="p" domain={[yMin - yPad, yMax + yPad]} tickFormatter={tickMoney} tick={{ fontSize: 10, fill: c.dim }} width={62} stroke={c.border} />
          <YAxis yAxisId="d" hide domain={[0, maxProb * 3]} />

          {/* Flat array (no Fragment wrappers), drawn on the P&L axis: recharts builds no scale
              for an axis with no series bound to it, so OI is mapped into the lower 30% of the
              plot (0 OI = the plot floor). */}
          {oi && oi.rows.reduce<React.ReactNode[]>((acc, r) => acc.concat([
            r.ceOi > 0 ? (
              <ReferenceArea key={`ce${r.strike}`} yAxisId="p" x1={r.strike - oi.half} x2={r.strike} y1={oiFloor} y2={oiY(r.ceOi)} ifOverflow="hidden"
                fill={c.call} fillOpacity={oi.ceTop.has(r.strike) ? 0.32 : 0.12} stroke="none"
                label={r.strike === oi.ce1 ? { value: `CE wall ${fmtStrike(r.strike)}`, position: 'top', fill: c.call, fontSize: 9 } : undefined} />
            ) : null,
            r.peOi > 0 ? (
              <ReferenceArea key={`pe${r.strike}`} yAxisId="p" x1={r.strike} x2={r.strike + oi.half} y1={oiFloor} y2={oiY(r.peOi)} ifOverflow="hidden"
                fill={c.put} fillOpacity={oi.peTop.has(r.strike) ? 0.32 : 0.12} stroke="none"
                label={r.strike === oi.pe1 ? { value: `PE wall ${fmtStrike(r.strike)}`, position: 'top', fill: c.put, fontSize: 9 } : undefined} />
            ) : null,
          ]), []).filter(Boolean)}

          {inX(bands.lo2) || inX(bands.hi2) ? (
            <ReferenceArea yAxisId="p" x1={Math.max(xMin, bands.lo2)} x2={Math.min(xMax, bands.hi2)} fill={c.blue} fillOpacity={0.04} stroke="none"
              label={{ value: '±2σ', position: 'insideTopLeft', fill: c.faint, fontSize: 9 }} />
          ) : null}
          <ReferenceArea yAxisId="p" x1={Math.max(xMin, bands.lo1)} x2={Math.min(xMax, bands.hi1)} fill={c.blue} fillOpacity={0.06} stroke="none"
            label={{ value: '±1σ', position: 'insideTop', fill: c.faint, fontSize: 9 }} />

          {showProb && (
            <Area yAxisId="d" dataKey="prob" type="monotone" stroke={c.amber} strokeOpacity={0.5} fill={c.amber} fillOpacity={0.08} isAnimationActive={false} dot={false} activeDot={false} />
          )}
          <Area yAxisId="p" dataKey="pos" type="linear" stroke="none" fill={c.green} fillOpacity={0.16} baseValue={0} isAnimationActive={false} activeDot={false} />
          <Area yAxisId="p" dataKey="neg" type="linear" stroke="none" fill={c.red} fillOpacity={0.14} baseValue={0} isAnimationActive={false} activeDot={false} />
          <ReferenceLine yAxisId="p" y={0} stroke={c.borderStrong} />
          <Line yAxisId="p" dataKey="exp" type="linear" stroke={c.red} strokeWidth={1.8} dot={false} isAnimationActive={false} name="At expiry" />
          <Line yAxisId="p" dataKey="today" type="monotone" stroke={c.blue} strokeWidth={1.6} dot={false} isAnimationActive={false} name="T+x" />
          {cmp && <Line yAxisId="p" dataKey="before" type="linear" stroke={c.dim} strokeWidth={1.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} name="Now" />}

          {breakevens.filter(inX).map(b => (
            <ReferenceLine key={b} yAxisId="p" x={b} stroke={c.amber} strokeDasharray="3 3" strokeOpacity={0.7}
              label={{ value: fmtStrike(Math.round(b)), position: 'insideBottom', fill: c.amber, fontSize: 9.5 }} />
          ))}
          {/* Both labels sat centred on top of their lines and collided whenever the
              what-if price was near spot (a level 1-2% away): each now reads AWAY
              from the other — the left one ends at its line, the right one starts there. */}
          <ReferenceLine yAxisId="p" x={spot} stroke={c.green} strokeWidth={1.4}
            label={{ value: `Index ${fmtStrike(Math.round(spot))}`, position: showWhatIf ? (whatIfSpot < spot ? 'insideTopLeft' : 'insideTopRight') : 'top', fill: c.green, fontSize: 10 }} />
          {showWhatIf && (
            <ReferenceLine yAxisId="p" x={whatIfSpot} stroke={c.amber} strokeDasharray="4 2"
              label={{ value: `What-if ${fmtStrike(Math.round(whatIfSpot))}`, position: whatIfSpot < spot ? 'insideTopRight' : 'insideTopLeft', fill: c.amber, fontSize: 10 }} />
          )}

          <Tooltip
            isAnimationActive={false}
            cursor={{ stroke: c.faint, strokeDasharray: '3 3' }}
            content={({ active, payload }: any) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              const mv = ((d.s / spot) - 1) * 100;
              return (
                <Box sx={{ bgcolor: c.panel, border: `1px solid ${c.borderStrong}`, borderRadius: 1, px: 1, py: 0.6, fontSize: 11, boxShadow: 3 }}>
                  <Box sx={{ fontWeight: 700 }}>{fmtStrike(Math.round(d.s))} <Box component="span" sx={{ color: c.dim, fontWeight: 400 }}>({mv >= 0 ? '+' : ''}{mv.toFixed(2)}%)</Box></Box>
                  <Box sx={{ color: c.red }}>At expiry: <b>{fmtMoney(d.exp / fx, ccy, { sign: true })}</b></Box>
                  <Box sx={{ color: c.blue }}>T+x: <b>{fmtMoney(d.today / fx, ccy, { sign: true })}</b></Box>
                  {d.before != null && <Box sx={{ color: c.dim }}>Now, at expiry: <b>{fmtMoney(d.before / fx, ccy, { sign: true })}</b></Box>}
                </Box>
              );
            }} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default React.memo(PayoffGraph);
